/**
 * Bootstrap the i18n registry with translations for every piece of static
 * game content (weapons, shields, consumables, engines, races, resources,
 * ship classes, ranks). This runs once when the shared package is imported,
 * so any code doing `t('item.weapon.pulse_blaster.name')` gets the right
 * string in both locales without any per-site wiring.
 *
 * The admin tool and server-side content loader are free to override these
 * entries at runtime via {@link registerTranslation} — the stock translations
 * defined here are just the baseline "shipped with the game" content that
 * seeds the database the first time the server boots.
 */
import { itemNameKey, itemDescriptionKey, raceNameKey, raceDescriptionKey, resourceNameKey, shipNameKey, rankNameKey, registerTranslation, } from './index.js';
import { WEAPONS, SHIELDS, ENGINES } from '../constants/weapons.js';
import { CONSUMABLES } from '../constants/consumables.js';
import { RESOURCE_INFO, SHIP_STATS, RACE_INFO, RANK_NAMES } from '../constants/game.js';
import { WEAPON_NAME_PT, WEAPON_DESC_PT, SHIELD_NAME_PT, SHIELD_DESC_PT, ENGINE_NAME_PT, ENGINE_DESC_PT, CONSUMABLE_NAME_PT, CONSUMABLE_DESC_PT, RACE_NAME_PT, RACE_DESC_PT, RESOURCE_NAME_PT, SHIP_NAME_PT, } from './content-pt.js';
/**
 * Registers en/es/pt translations for a content key.
 *
 * The English and Spanish strings always come from the canonical
 * constant definitions (the source of truth shipped in the binary).
 * The Portuguese string is taken from the optional inline `…Pt` field
 * on the constant first, then the static fallback map in
 * {@link content-pt.ts}, then omitted entirely (so {@link t} falls
 * back to English at runtime, the documented graceful-degradation
 * path for partially-translated locales).
 */
function registerTriplet(key, en, es, pt) {
    if (!en && !es && !pt)
        return;
    registerTranslation(key, { en, es, pt });
}
export function registerContentTranslations() {
    for (const [id, w] of Object.entries(WEAPONS)) {
        registerTriplet(itemNameKey('weapon', id), w.name, w.nameEs, w.namePt ?? WEAPON_NAME_PT[id]);
        registerTriplet(itemDescriptionKey('weapon', id), w.description, w.descriptionEs, w.descriptionPt ?? WEAPON_DESC_PT[id]);
    }
    for (const [id, s] of Object.entries(SHIELDS)) {
        registerTriplet(itemNameKey('shield', id), s.name, s.nameEs, s.namePt ?? SHIELD_NAME_PT[id]);
        registerTriplet(itemDescriptionKey('shield', id), s.description, s.descriptionEs, s.descriptionPt ?? SHIELD_DESC_PT[id]);
    }
    for (const [id, e] of Object.entries(ENGINES)) {
        registerTriplet(itemNameKey('engine', id), e.name, e.nameEs, e.namePt ?? ENGINE_NAME_PT[id]);
        registerTriplet(itemDescriptionKey('engine', id), e.description, e.descriptionEs, e.descriptionPt ?? ENGINE_DESC_PT[id]);
    }
    for (const [id, c] of Object.entries(CONSUMABLES)) {
        registerTriplet(itemNameKey('consumable', id), c.name, c.nameEs, c.namePt ?? CONSUMABLE_NAME_PT[id]);
        registerTriplet(itemDescriptionKey('consumable', id), c.description, c.descriptionEs, c.descriptionPt ?? CONSUMABLE_DESC_PT[id]);
    }
    for (const [rid, info] of Object.entries(RACE_INFO)) {
        const n = Number(rid);
        registerTriplet(raceNameKey(n), info.name, info.nameEs, info.namePt ?? RACE_NAME_PT[n]);
        registerTriplet(raceDescriptionKey(n), info.description, info.descriptionEs, info.descriptionPt ?? RACE_DESC_PT[n]);
    }
    for (const [tier, info] of Object.entries(RESOURCE_INFO)) {
        const n = Number(tier);
        registerTriplet(resourceNameKey(n), info.name, info.nameEs, info.namePt ?? RESOURCE_NAME_PT[n]);
    }
    for (const [sc, info] of Object.entries(SHIP_STATS)) {
        const n = Number(sc);
        registerTriplet(shipNameKey(n), info.name, info.nameEs, info.namePt ?? SHIP_NAME_PT[n]);
    }
    for (const [rk, info] of Object.entries(RANK_NAMES)) {
        registerTriplet(rankNameKey(Number(rk)), info.en, info.es, info.pt);
    }
}
registerContentTranslations();
//# sourceMappingURL=content-translations.js.map